import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, LogOut, Repeat2, Send, X } from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton } from '@iheart/ui';
import { useFocusTrap } from '../lib/useFocusTrap';

/* ── Types ──────────────────────────────────────────────────────── */

interface ConfirmOptions {
  title: string;
  message: string;
  /** Label for the primary action button (default: "Confirm") */
  confirmLabel?: string;
  /** Visual style for the confirm button (default: "danger") */
  variant?: 'danger' | 'primary' | 'logout' | 'switch' | 'publish';
  /** Label for the cancel button (default: "Cancel") */
  cancelLabel?: string;
}

interface ConfirmContextValue {
  /**
   * Show a confirmation dialog and return a promise that resolves
   * to `true` when the user confirms, or `false` when they cancel.
   *
   * Usage:
   *   const confirmed = await confirm({
   *     title: 'Delete post?',
   *     message: 'This cannot be undone.',
   *   });
   *   if (confirmed) { ... }
   */
  confirm(options: ConfirmOptions): Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/* ── Hook ───────────────────────────────────────────────────────── */

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx.confirm;
}

/* ── Provider ───────────────────────────────────────────────────── */

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ ...options, open: true });
    });
  }, []);

  function handleClose(result: boolean) {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState(null);
  }

  const trapRef = useFocusTrap<HTMLDivElement>(!!state?.open);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AnimatePresence>
        {state?.open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm"
              onClick={() => handleClose(false)}
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            >
              <div
                ref={trapRef}
                className="w-full max-w-md overflow-hidden rounded-2xl border border-white/30 bg-white shadow-2xl"
                role="alertdialog"
                aria-labelledby="confirm-title"
                aria-describedby="confirm-desc"
              >
                {/* Header */}
                <div className="flex items-start gap-3 px-5 pt-5">
                  {state.variant === 'publish' ? (
                    <div
                      className="flex size-10 shrink-0 items-center justify-center rounded-xl text-white"
                      style={{ backgroundImage: brand.gradient.primary }}
                    >
                      <Send className="size-5" />
                    </div>
                  ) : state.variant === 'logout' ? (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                      <LogOut className="size-5" />
                    </div>
                  ) : state.variant === 'switch' ? (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-500">
                      <Repeat2 className="size-5" />
                    </div>
                  ) : (
                    <div
                      className={
                        'flex size-10 shrink-0 items-center justify-center rounded-xl ' +
                        (state.variant === 'primary'
                          ? 'bg-blue-50 text-blue-500'
                          : 'bg-red-50 text-red-500')
                      }
                    >
                      <AlertTriangle className="size-5" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h2
                      id="confirm-title"
                      className="text-sm font-semibold text-neutral-800"
                    >
                      {state.title}
                    </h2>
                    <p
                      id="confirm-desc"
                      className="mt-1 text-sm text-neutral-500"
                    >
                      {state.message}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleClose(false)}
                    className="shrink-0 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                    aria-label="Close"
                  >
                    <X className="size-4" />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 px-5 pb-5 pt-4">
                  <button
                    type="button"
                    onClick={() => handleClose(false)}
                    className="rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-600 transition-all hover:border-neutral-300 hover:bg-neutral-50 active:scale-[0.98]"
                  >
                    {state.cancelLabel ?? (state.variant === 'publish' ? 'Stop this madness' : 'Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClose(true)}
                    className={
                      'rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.98] ' +
                      (state.variant === 'publish'
                        ? 'bg-gradient-to-r from-pink-500 to-indigo-500'
                        : state.variant === 'primary' || state.variant === 'switch'
                          ? 'bg-gradient-to-r from-pink-500 to-indigo-500'
                          : state.variant === 'logout'
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-red-500 hover:bg-red-600')
                    }
                  >
                    <span>{state.confirmLabel ?? 'Confirm'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  );
}
